/**
 * Theme shape (corner radius) — UPT-1955.
 *
 * Source unique de l'echelle de rayons du theme : le configurateur (apercu)
 * ET l'application reelle (portail, site public) lisent cette echelle, de
 * sorte qu'un rayon « carre » rende bien carre partout.
 *
 * L'echelle est PROPORTIONNELLE (rapport constant) : chaque marche est un
 * multiple du rayon de base, donc un rayon de base a 0 donne 0 partout — le
 * defaut precedent (base + 4px / base + 8px) laissait `rounded-xl` a 4px et
 * `rounded-2xl` a 8px meme en mode « carre », d'ou le retour UAT « BORD carre
 * reste relativement rond ».
 *
 * Les rapports sont cales sur les valeurs par defaut de app/styles/tailwind.css
 * (sm 2 / md 4 / lg 8 / xl 12 / 2xl 16 / 3xl 24) : avec un rayon de base de 8px
 * — la valeur par defaut de `resolve_theme_shape_defaults` — l'echelle
 * reproduit exactement ces valeurs.
 *
 * `rounded` NU (utilise ~360 fois dans l'app) et `rounded-xs` ne lisaient pas
 * l'echelle : Tailwind les compile en dur (0.25rem / 0.125rem). Ils restaient
 * donc a 4px / 2px quel que soit le rayon choisi. tailwind.css les rattache
 * maintenant a `--radius-md` / `--radius-xs` ; theme_tailwind_radius.test.ts
 * garde ce lien.
 */

export type ThemeRadiusStep = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export const theme_radius_ratios: Record<ThemeRadiusStep, number> = {
  // `rounded-xs` : 2px par defaut, comme le `--radius-xs` de Tailwind. Sans
  // variable dediee il restait fige a 0.125rem quel que soit le rayon du theme.
  xs: 0.25,
  sm: 0.25,
  md: 0.5,
  lg: 1,
  xl: 1.5,
  '2xl': 2,
  '3xl': 3,
};

export const theme_corner_radius_min = 0;
export const theme_corner_radius_max = 32;

/** Les quatre crans proposes par le configurateur (carre / leger / arrondi / tres arrondi). */
export const theme_corner_radius_options = [0, 8, 16, 24] as const;

const clamp_corner_radius = (cornerRadius: number | null | undefined) => {
  const value =
    typeof cornerRadius === 'number' && Number.isFinite(cornerRadius) ? cornerRadius : 8;
  return Math.min(Math.max(Math.round(value), theme_corner_radius_min), theme_corner_radius_max);
};

/** Echelle en pixels (nombres) — pour les styles inline de l'apercu. */
export const resolve_theme_radius_values = (
  cornerRadius: number | null | undefined
): Record<ThemeRadiusStep, number> => {
  const base = clamp_corner_radius(cornerRadius);
  return {
    xs: Math.round(base * theme_radius_ratios.xs),
    sm: Math.round(base * theme_radius_ratios.sm),
    md: Math.round(base * theme_radius_ratios.md),
    lg: Math.round(base * theme_radius_ratios.lg),
    xl: Math.round(base * theme_radius_ratios.xl),
    '2xl': Math.round(base * theme_radius_ratios['2xl']),
    '3xl': Math.round(base * theme_radius_ratios['3xl']),
  };
};

/** Echelle en chaines CSS (`"8px"`). */
export const resolve_theme_radius_scale = (
  cornerRadius: number | null | undefined
): Record<ThemeRadiusStep, string> => {
  const values = resolve_theme_radius_values(cornerRadius);
  return {
    xs: `${values.xs}px`,
    sm: `${values.sm}px`,
    md: `${values.md}px`,
    lg: `${values.lg}px`,
    xl: `${values.xl}px`,
    '2xl': `${values['2xl']}px`,
    '3xl': `${values['3xl']}px`,
  };
};

/**
 * Variables CSS du rayon — emises a l'identique par le runtime du portail
 * (`create_theme_css_variables`) et par l'apercu du configurateur
 * (`themeToCSSVars`).
 */
export const create_theme_radius_css_variables = (
  cornerRadius: number | null | undefined
): Record<string, string> => {
  const scale = resolve_theme_radius_scale(cornerRadius);
  return {
    '--radius-xs': scale.xs,
    '--radius-sm': scale.sm,
    '--radius-md': scale.md,
    '--radius-lg': scale.lg,
    '--radius-xl': scale.xl,
    '--radius-2xl': scale['2xl'],
    '--radius-3xl': scale['3xl'],
    '--radius': scale.lg,
  };
};
